import { DateTimeFormatter, Instant, LocalDate, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale_en-us";
import last from "lodash/last";
import { ComponentPropsWithoutRef, Fragment } from "react";
import { BloodGlucoseForm } from "../../../../model/BloodGlucoseForm";
import BloodGlucoseFormContext from "../../../../model/BloodGlucoseFormContext";
import { BloodGlucoseObservation } from "../../../../model/BloodGlucoseObservation";
import { BloodGlucoseObservationStatistics } from "../../../../model/BloodGlucoseObservations";
import { BloodGlucoseReading } from "../../../../model/BloodGlucoseReading";
import cn from "../../../../util/cn";
import { Cell, HeaderCell } from "../../Cell";
import OverviewCell from "./OverviewCell";

type OverviewTableProps = ComponentPropsWithoutRef<"div"> & {
  dates: LocalDate[];
  observations: BloodGlucoseObservation[];
  statistics: BloodGlucoseObservationStatistics;
  lastReviewTime: Instant;
  selectedObservation: BloodGlucoseObservation | null;
  onObservationSelected: (observation: BloodGlucoseObservation) => void;
};

const OverviewTable = ({
  dates,
  observations,
  statistics,
  lastReviewTime,
  selectedObservation,
  onObservationSelected,
  className,
  ...otherProps
}: OverviewTableProps) => {
  const mealContexts = ["fasting", "breakfast", "lunch", "dinner"] as const;

  const isSelectedObservation = (observation: BloodGlucoseObservation | undefined) =>
    selectedObservation !== null && observation?.id === selectedObservation.id;

  return (
    <div className={className} {...otherProps}>
      <div className="grid grid-cols-[min-content_1fr_1fr_1fr_1fr] gap-px ps-px">
        <Cell />
        <StatisticsCell
          className="mb-3 rounded-l-md"
          title="Median fasting"
          value={statistics["fasting"]?.medianBloodGlucoseInMilligramsPerDeciliter}
        />
        <StatisticsCell
          className="mb-3"
          title="Median breakfast"
          value={statistics["breakfast"]?.medianBloodGlucoseInMilligramsPerDeciliter}
        />
        <StatisticsCell
          className="mb-3"
          title="Median lunch"
          value={statistics["lunch"]?.medianBloodGlucoseInMilligramsPerDeciliter}
        />
        <StatisticsCell
          className="mb-3 rounded-r-md"
          title="Median dinner"
          value={statistics["dinner"]?.medianBloodGlucoseInMilligramsPerDeciliter}
        />
        <Cell />
        <HeaderCell>Fasting</HeaderCell>
        <HeaderCell>Breakfast</HeaderCell>
        <HeaderCell>Lunch</HeaderCell>
        <HeaderCell>Dinner</HeaderCell>
        {dates.map((date) => {
          const dateString = dayMonthFormatter.format(date);
          const dayOfWeekString = dayOfWeekFormatter.format(date);
          const dateStart = date.atStartOfDay(ZoneId.SYSTEM).toInstant();
          return (
            <Fragment key={`date-${dateString}`}>
              <HeaderCell className="justify-start">
                <span className="inline-block" style={{ minWidth: `${dateString.length}ch` }}>
                  {dateString}
                </span>
                {dayOfWeekString}
              </HeaderCell>
              {mealContexts.map((mealContext) => {
                const observation = observationForCell({
                  observations,
                  date,
                  mealContext: mealContext,
                });
                const reading = observation?.reading;
                const isNew = observation?.dateTime.toInstant().isAfter(lastReviewTime);
                const isEmpty = !reading;
                const isDisabled = isEmpty && dateStart.isAfter(Instant.now());
                const status = isDisabled ? "disabled" : isNew ? "new" : "default";
                return (
                  <OverviewCell
                    key={`reading-cell-${date}-${mealContext}`}
                    reading={reading}
                    status={status}
                    onClick={() => {
                      if (!isEmpty) {
                        onObservationSelected(observation!);
                      }
                    }}
                    className={cn(
                      isEmpty ? "cursor-default" : "cursor-pointer",
                      isEmpty
                        ? ""
                        : isSelectedObservation(observation)
                          ? "ring-2 ring-inset ring-blue-400 print:ring-transparent"
                          : "ring-blue-400 hover:ring-2 hover:ring-inset hover:ring-blue-400/50 print:ring-transparent print:hover:ring-transparent"
                    )}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

type StatisticsCellProps = {
  title: string;
  value?: number;
} & ComponentPropsWithoutRef<typeof Cell>;

const StatisticsCell = ({ title, value, className, ...otherProps }: StatisticsCellProps) => (
  <Cell
    className={cn(
      "flex flex-col items-center justify-center bg-gray-50 p-2 outline outline-1 outline-gray-300",
      className
    )}
    {...otherProps}
  >
    <p className="truncate text-primary/60">{title}</p>
    <p className="font-semibold text-primary/90">{value ?? "-"}</p>
  </Cell>
);

const dayOfWeekFormatter = DateTimeFormatter.ofPattern("EEEE").withLocale(Locale.US);
const dayMonthFormatter = DateTimeFormatter.ofPattern("MM/dd").withLocale(Locale.US);

const observationForCell = ({
  observations,
  date,
  mealContext,
}: {
  observations: BloodGlucoseObservation[];
  date: LocalDate;
  mealContext: BloodGlucoseFormContext;
}):
  | (BloodGlucoseObservation & { reading: BloodGlucoseReading; form: BloodGlucoseForm })
  | undefined =>
  last(
    observations
      .filter((observation) => Boolean(observation.reading))
      .filter((observation) => observation.form?.context === mealContext)
      .filter((observation) => observation.reading!.dateTime.toLocalDate().equals(date))
      .map((observation) => ({
        ...observation,
        reading: observation.reading!,
        form: observation.form!,
      }))
  );

export default OverviewTable;
